import { useEffect, useState } from 'react'
import { featureFlagsApi } from '../api/featureFlags'

/**
 * F-063 (TASK-0317, REQ-258-001): `useFeatureFlag('chiave')` dice se una funzione nuova e' accesa per
 * l'utente corrente. Una sola chiamata a `GET /api/feature-flags/me` per sessione (cache di modulo,
 * condivisa fra tutti i componenti); un flag sconosciuto o un errore di rete valgono "spento", cosi'
 * una funzione sotto flag non compare mai per sbaglio. `resetFeatureFlagCache()` va chiamata al logout
 * e dopo una modifica dei flag.
 */
let cache: Promise<Record<string, boolean>> | null = null
const listeners = new Set<() => void>()

function loadFlags(): Promise<Record<string, boolean>> {
  cache ??= featureFlagsApi.me().then(r => r.flags).catch(() => ({} as Record<string, boolean>))
  return cache
}

/** Le flag dell'utente (stessa cache di `useFeatureFlag`); per chi deve attendere la risposta prima di decidere. */
export function getFeatureFlags(): Promise<Record<string, boolean>> {
  return loadFlags()
}

export function resetFeatureFlagCache(): void {
  cache = null
  listeners.forEach(l => l())
}

export function useFeatureFlag(key: string): boolean {
  const [enabled, setEnabled] = useState(false)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    const onReset = () => setVersion(v => v + 1)
    listeners.add(onReset)
    return () => { listeners.delete(onReset) }
  }, [])

  useEffect(() => {
    let alive = true
    void loadFlags().then(flags => { if (alive) setEnabled(flags[key] === true) })
    return () => { alive = false }
  }, [key, version])

  return enabled
}

/** Tutte le flag dell'utente in una mappa (stessa cache di `useFeatureFlag`): serve al menu, che filtra le voci per flag. */
export function useFeatureFlags(): Record<string, boolean> {
  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const [version, setVersion] = useState(0)

  useEffect(() => {
    const onReset = () => setVersion(v => v + 1)
    listeners.add(onReset)
    return () => { listeners.delete(onReset) }
  }, [])

  useEffect(() => {
    let alive = true
    void loadFlags().then(f => { if (alive) setFlags(f) })
    return () => { alive = false }
  }, [version])

  return flags
}
